import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { 
  User, 
  Save,
  CreditCard,
  Upload,
  X,
  Users,
  Shield
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from '../hooks/use-auth';
import { apiRequest, queryClient } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../components/ui/form';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import TeamPage from './team-page';
import RoleSetupPage from './role-setup-page';

// Phone number validation - accepts various formats
const validatePhone = (phone: string | undefined): boolean => {
  if (!phone || phone.trim() === "") return true; // Optional field
  
  // Remove all non-digit characters except + at the start
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
  
  // Check if it starts with + (international) or is just digits
  if (cleaned.startsWith('+')) {
    // International format: + followed by 10-15 digits
    const digits = cleaned.substring(1);
    return /^[0-9]{10,15}$/.test(digits);
  } else {
    // US format: 10 digits (or 11 if starts with 1)
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return /^1[0-9]{10}$/.test(cleaned);
    }
    return /^[0-9]{10}$/.test(cleaned);
  }
};

// Form schemas
const profileFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  companyName: z.string().min(1, "Company name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string()
    .optional()
    .refine((val) => validatePhone(val), {
      message: "Please enter a valid phone number (e.g., (555) 123-4567, 555-123-4567, or +1 555 123 4567)",
    }),
  address: z.string()
    .optional()
    .refine((val) => !val || val.trim() === "" || val.trim().length >= 5, {
      message: "Address must be at least 5 characters if provided",
    })
    .refine((val) => !val || val.trim() === "" || /^[a-zA-Z0-9\s\-\.,#]+$/.test(val), {
      message: "Address contains invalid characters",
    }),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string()
    .optional()
    .refine((val) => !val || val.trim() === "" || /^\d{5}(-\d{4})?$/.test(val.trim()), {
      message: "ZIP code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)",
    }),
  logoUrl: z.string().optional(),
});

const securityFormSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;
type SecurityFormValues = z.infer<typeof securityFormSchema>;

/** Subscription usage for AI roof reports (from /api/protected/subscription/status) */
interface SubscriptionUsage {
  plan: { planName: string; aiUsageLimit: number | null };
  usage: { currentAiUsage: number; maxAiUsage: number | null; aiUsageRemaining: number | string };
}

function AiRoofReportsTable() {
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuery<SubscriptionUsage>({
    queryKey: ['/api/protected/subscription/status'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/protected/subscription/status');
      if (!res.ok) throw new Error('Failed to load subscription');
      const json = await res.json();
      return { plan: json.plan, usage: json.usage };
    },
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: true,
  });

  if (isError && !data) {
    return (
      <div className="rounded-xl border-2 border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/20 p-5 shadow-sm">
        <h4 className="text-base font-semibold text-[var(--dash-text)] mb-2">AI Roof Reports (this month)</h4>
        <p className="text-sm text-[var(--dash-text-muted)]">Could not load usage. <a href="/billing" className="text-green-600 hover:underline">Open Billing</a> to see your plan and usage.</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="rounded-xl border-2 border-[var(--dash-border)] bg-[var(--dash-card)] p-5 shadow-sm">
        <h4 className="text-base font-semibold text-[var(--dash-text)] mb-2">AI Roof Reports (this month)</h4>
        <p className="text-sm text-[var(--dash-text-muted)]">{isLoading ? 'Loading…' : 'Unable to load. Check your connection or try again.'}</p>
      </div>
    );
  }

  const { plan, usage } = data;
  const planName = (plan.planName || '').toLowerCase();
  const isProOrBusiness = planName === 'pro' || planName === 'business';
  const limit = plan.aiUsageLimit;
  const used = Number(usage.currentAiUsage) || 0;
  const included = limit != null && limit > 0 ? limit : (isProOrBusiness ? 5 : 0);
  const remaining = usage.aiUsageRemaining;

  return (
    <div className="rounded-xl border-2 border-[var(--dash-border)] bg-[var(--dash-card)] overflow-hidden shadow-sm">
      <h4 className="text-base font-semibold text-[var(--dash-text)] px-5 py-4 border-b border-[var(--dash-border)]">
        AI Roof Reports (this month)
      </h4>
      <div className="p-5">
        {!isProOrBusiness ? (
          <>
            <p className="text-sm text-[var(--dash-text-muted)] mb-2">
              Not included in your plan. Upgrade to Pro or Business to get 5 reports per month.
            </p>
            <p className="text-xs text-[var(--dash-text-muted)]">Current plan: {plan.planName || 'Basic'}</p>
          </>
        ) : (
          <>
            <p className="text-xs text-[var(--dash-text-muted)] mb-3">Plan: {plan.planName === 'business' ? 'Business' : 'Pro'}</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--dash-text-muted)] border-b border-[var(--dash-border)]">
                  <th className="pb-2 pr-4 font-medium">Used</th>
                  <th className="pb-2 pr-4 font-medium">Included</th>
                  <th className="pb-2 font-medium">Remaining</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 pr-4 text-[var(--dash-text)]">{used}</td>
                  <td className="py-2 pr-4 text-[var(--dash-text)]">{included}</td>
                  <td className="py-2 text-[var(--dash-text)]">
                    {typeof remaining === 'string' ? remaining : remaining}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [location] = useLocation();
  const { user, isLoading, refreshSession } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [contractorSettings, setContractorSettings] = useState<any | null>(null);
  const [showAiRoofMeasurement, setShowAiRoofMeasurement] = useState(false);

  const activeTab = location === "/settings/team" ? "team" : location === "/settings/role-setup" ? "role-setup" : "profile";
  const userRole = (user as { role?: string })?.role;
  const canManageTeam = !isLoading && (userRole === "owner" || userRole === "admin" || userRole === "contractor");
  const isOwner = !isLoading && (userRole === "owner" || userRole === "contractor");
  
  // Force refetch user data when settings page loads to get fresh data from database
  useEffect(() => {
    // Refetch user data to ensure we have the latest from database
    queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    refreshSession().catch(err => {
      console.error('Failed to refresh session:', err);
    });
  }, []); // Only run once on mount

  // Load contractor settings (for feature toggles like AI Roof Measurement)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiRequest("GET", "/api/protected/contractor/settings");
        if (!response.ok) return;
        const data = await response.json();
        setContractorSettings(data);
        setShowAiRoofMeasurement(Boolean((data as any).showAiRoofMeasurement));
      } catch (error) {
        console.error("Failed to load contractor settings:", error);
      }
    };
    loadSettings();
  }, []);
  
  // Redirect to login if not authenticated
  if (!user && !isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">
            Authentication Required
          </h1>
          <p className="text-slate-600 mb-6">
            Please log in to access your settings.
          </p>
          <Button onClick={() => window.location.href = '/simple-login'}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Profile form - initialize with user data or empty values
  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      logoUrl: "",
    },
  });

  // CRITICAL: Update form when user data loads or changes
  // This ensures form always shows current database values
  useEffect(() => {
    if (user) {
      console.log('🔄 Settings page - Updating form with user data:', {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        address: user.address,
        city: user.city,
        state: user.state,
        zip: user.zip,
        logoUrl: user.logoUrl ? 'present' : 'not set'
      });
      
      // Reset form with current user data from database
      profileForm.reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        companyName: user.companyName || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip: user.zip || "",
        logoUrl: user.logoUrl || "",
      });
      
      // Update logo preview when user data changes
      if (user.logoUrl) {
        setLogoPreview(user.logoUrl);
      } else {
        setLogoPreview(null);
      }
    }
  }, [user, profileForm]);

  // Security form
  const securityForm = useForm<SecurityFormValues>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Compress and convert image to base64
  const compressLogo = (file: File, maxSizeKB: number = 200): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions (max 400px on longest side for logo)
          const maxDimension = 400;
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to create canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with quality compression (logos work well as JPEG)
          let quality = 0.85;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // If still too large, reduce quality further
          while (dataUrl.length > maxSizeKB * 1024 && quality > 0.3) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error(`Failed to load image: ${file.name}`));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });
  };

  // Handle logo upload
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (PNG, JPG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      // Compress and convert to base64
      const base64String = await compressLogo(file, 200); // Max 200KB after compression
      setLogoPreview(base64String);
      profileForm.setValue("logoUrl", base64String);
      toast({
        title: "Logo loaded",
        description: "Logo ready to save. Click 'Save Changes' to update your profile.",
      });
    } catch (error) {
      console.error('Error compressing logo:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to process the image file",
        variant: "destructive",
      });
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoPreview(null);
    profileForm.setValue("logoUrl", "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormValues) => {
      // Always include logoUrl from form state, even if it's the same
      const payload = {
        ...data,
        logoUrl: profileForm.getValues("logoUrl") || data.logoUrl || null
      };
      console.log('🔍 Submitting profile update - logoUrl:', payload.logoUrl ? `Present (${payload.logoUrl.length} chars)` : 'Not provided');
      const res = await apiRequest("PATCH", `/api/protected/profile`, payload);
      return await res.json();
    },
    onSuccess: async (data) => {
      // Update logo preview if logoUrl is in response
      if (data.user?.logoUrl) {
        setLogoPreview(data.user.logoUrl);
      } else if (data.user?.logoUrl === null || data.user?.logoUrl === undefined) {
        // Explicitly handle null to clear preview if logo was removed
        setLogoPreview(null);
      }
      
      // Update localStorage with new user data
      if (data.user) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Update React Query cache immediately
        queryClient.setQueryData(["/api/user"], updatedUser);
      }
      
      // Reset form with updated data to ensure UI reflects changes
      if (data.user) {
        profileForm.reset({
          firstName: data.user.firstName || "",
          lastName: data.user.lastName || "",
          companyName: data.user.companyName || "",
          email: data.user.email || "",
          phone: data.user.phone || "",
          address: data.user.address || "",
          city: data.user.city || "",
          state: data.user.state || "",
          zip: data.user.zip || "",
          logoUrl: data.user.logoUrl || "",
        });
      }
      
      // Invalidate and refetch user data to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: SecurityFormValues) => {
      const res = await apiRequest("POST", `/api/protected/change-password`, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      return await res.json();
    },
    onSuccess: () => {
      securityForm.reset({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password change failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  function onProfileSubmit(data: ProfileFormValues) {
    updateProfileMutation.mutate(data);
  }

  function onSecuritySubmit(data: SecurityFormValues) {
    changePasswordMutation.mutate(data);
  }

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24 flex flex-col items-center">
            <div className="w-full space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Settings</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your account settings and preferences.</p>
              </div>

            <Tabs value={activeTab} className="w-full space-y-4">
              <TabsList className="flex gap-1 border-b border-[var(--dash-border)] bg-transparent p-0 h-auto rounded-none w-full">
                <Link href="/settings" className="flex-1 min-w-0">
                  <TabsTrigger value="profile" className="w-full justify-center dash-tab-active data-[state=inactive]:dash-tab-inactive rounded-t-lg px-4 py-2.5">
                    <User className="h-4 w-4 mr-2" />
                    Profile & Settings
                  </TabsTrigger>
                </Link>
                {canManageTeam && (
                  <Link
                    href="/settings/team"
                    className={`flex-1 min-w-0 inline-flex items-center justify-center rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                      activeTab === "team" ? "dash-tab-active" : "dash-tab-inactive"
                    }`}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Team
                  </Link>
                )}
                {isOwner && (
                  <Link
                    href="/settings/role-setup"
                    className={`flex-1 min-w-0 inline-flex items-center justify-center rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                      activeTab === "role-setup" ? "dash-tab-active" : "dash-tab-inactive"
                    }`}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Role setup
                  </Link>
                )}
              </TabsList>
              <TabsContent value="profile" className="space-y-4 mt-0">
                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Profile Information</h2>
                    <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Update your account profile information and contact details.</p>
                  </div>
                  <div className="p-6">
                    <Form {...profileForm}>
                      <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-8">
                        <div className="flex flex-col md:flex-row gap-8">
                          <div className="md:w-2/3 space-y-4">
                            <h3 className="text-lg font-medium">Personal Information</h3>
                            
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                              <FormField
                                control={profileForm.control}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>First Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="John" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={profileForm.control}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-slate-800 dark:text-slate-200 font-medium">Last Name</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Doe" {...field} className="remodra-input" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <FormField
                              control={profileForm.control}
                              name="companyName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-800 dark:text-slate-200 font-medium">Company Name</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Your Company LLC" {...field} className="remodra-input" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-slate-800 dark:text-slate-200 font-medium">Email</FormLabel>
                                  <FormControl>
                                    <Input type="email" placeholder="john@example.com" {...field} className="remodra-input" />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Phone</FormLabel>
                                  <FormControl>
                                    <Input placeholder="(555) 123-4567" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <h3 className="text-lg font-medium pt-4">Address Information</h3>
                            
                            <FormField
                              control={profileForm.control}
                              name="address"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Street Address</FormLabel>
                                  <FormControl>
                                    <Input placeholder="123 Main St" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              <FormField
                                control={profileForm.control}
                                name="city"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>City</FormLabel>
                                    <FormControl>
                                      <Input placeholder="New York" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={profileForm.control}
                                name="state"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>State</FormLabel>
                                    <FormControl>
                                      <Input placeholder="NY" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={profileForm.control}
                                name="zip"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>ZIP Code</FormLabel>
                                    <FormControl>
                                      <Input placeholder="10001" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            <div className="mt-6 space-y-3">
                              <h3 className="text-lg font-medium">Tools & Features</h3>
                              <div className="flex items-start justify-between gap-4 rounded-lg border border-[var(--dash-border)] bg-[var(--dash-card-soft)] px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-[var(--dash-text)]">Show AI Roof Measurement tool</p>
                                  <p className="text-xs text-[var(--dash-text-muted)] mt-0.5">
                                    When enabled, the AI Roof Measurement tool appears in your main menu and tools. Off by default for non‑roofing contractors.
                                  </p>
                                </div>
                                <Switch
                                  checked={showAiRoofMeasurement}
                                  onCheckedChange={async (checked) => {
                                    setShowAiRoofMeasurement(checked);
                                    try {
                                      // Include pdfTemplate when available for backwards compatibility
                                      const current = contractorSettings || {};
                                      const pdfTemplate =
                                        (current as any).pdfTemplate ||
                                        (current as any).pdf_template ||
                                        undefined;

                                      const payload: any = { showAiRoofMeasurement: checked };
                                      if (pdfTemplate) {
                                        payload.pdfTemplate = pdfTemplate;
                                      }

                                      const res = await apiRequest("POST", "/api/protected/contractor/settings", payload);
                                      if (res.ok) {
                                        const data = await res.json();
                                        setContractorSettings(data.settings || data);
                                        try {
                                          window.dispatchEvent(
                                            new CustomEvent('contractor-settings-updated', {
                                              detail: { showAiRoofMeasurement: checked },
                                            })
                                          );
                                        } catch (e) {
                                          console.error('Failed to dispatch contractor-settings-updated event', e);
                                        }
                                      } else {
                                        // Log server message (if any) to help debug
                                        try {
                                          const body = await res.json().catch(() => null);
                                          console.error('Failed to save showAiRoofMeasurement setting', {
                                            status: res.status,
                                            body,
                                          });
                                        } catch (e) {
                                          console.error('Failed to read error response for showAiRoofMeasurement', e);
                                        }
                                        setShowAiRoofMeasurement(!checked);
                                        toast({
                                          title: "Could not save setting",
                                          description: "The AI Roof Measurement toggle could not be saved. Please try again.",
                                          variant: "destructive",
                                        });
                                      }
                                    } catch (error) {
                                      console.error("Failed to update contractor settings:", error);
                                      setShowAiRoofMeasurement(!checked);
                                      toast({
                                        title: "Could not save setting",
                                        description: "The AI Roof Measurement toggle could not be saved. Please try again.",
                                        variant: "destructive",
                                      });
                                    }
                                  }}
                                />
                              </div>
                              <div className="mt-4">
                                <AiRoofReportsTable />
                              </div>
                            </div>
                          </div>

                          <div className="md:w-1/3 space-y-4">
                            <div className="text-center">
                              <div className="relative inline-block mb-4">
                                {logoPreview ? (
                                  <div className="relative">
                                    <img 
                                      src={logoPreview} 
                                      alt="Company Logo" 
                                      className="h-32 w-32 mx-auto rounded-lg object-cover border-2 border-slate-300 dark:border-slate-600"
                                    />
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                                      onClick={handleRemoveLogo}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Avatar className="h-32 w-32 mx-auto">
                                    <AvatarImage src="" alt="Company Logo" />
                                    <AvatarFallback className="text-2xl">
                                      {user?.companyName?.charAt(0) || user?.firstName?.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                              </div>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                className="hidden"
                                id="logo-upload"
                              />
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm" 
                                className="remodra-button-outline flex items-center justify-center mx-auto"
                                onClick={() => fileInputRef.current?.click()}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {logoPreview ? "Change Logo" : "Upload Logo"}
                              </Button>
                              <p className="text-xs text-slate-500 mt-2">
                                Company logo will appear on estimates and invoices
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button 
                            type="submit" 
                            disabled={updateProfileMutation.isPending}
                            className="remodra-button flex items-center justify-center"
                          >
                            {updateProfileMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Changes
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Security</h2>
                    <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Update your password and security settings.</p>
                  </div>
                  <div className="p-6">
                    <Form {...securityForm}>
                      <form onSubmit={securityForm.handleSubmit(onSecuritySubmit)} className="space-y-4">
                        <FormField
                          control={securityForm.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Current Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="Enter current password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>New Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="Enter new password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Confirm New Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="Confirm new password" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="flex justify-end">
                          <Button 
                            type="submit" 
                            disabled={changePasswordMutation.isPending}
                            variant="outline"
                            className="remodra-button-outline flex items-center justify-center"
                          >
                            {changePasswordMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                                Updating...
                              </>
                            ) : (
                              'Update Password'
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Notifications</h2>
                    <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your notification preferences.</p>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="email-notifications">Email Notifications</Label>
                        <p className="text-sm text-slate-600">Receive notifications via email</p>
                      </div>
                      <Switch id="email-notifications" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="project-updates">Project Updates</Label>
                        <p className="text-sm text-slate-600">Notifications for project status changes</p>
                      </div>
                      <Switch id="project-updates" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="estimate-notifications">Estimate Notifications</Label>
                        <p className="text-sm text-slate-600">Notifications for estimate updates</p>
                      </div>
                      <Switch id="estimate-notifications" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="invoice-reminders">Invoice Reminders</Label>
                        <p className="text-sm text-slate-600">Reminders for overdue invoices</p>
                      </div>
                      <Switch id="invoice-reminders" defaultChecked />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label htmlFor="appointment-reminders">Appointment Reminders</Label>
                        <p className="text-sm text-slate-600">Reminders for upcoming appointments</p>
                      </div>
                      <Switch id="appointment-reminders" defaultChecked />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[var(--dash-border)]">
                    <h2 className="text-lg font-semibold text-[var(--dash-text)]">Billing & Subscription</h2>
                    <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your subscription and billing information.</p>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-medium">{user?.plan === 'basic' ? 'Basic Plan' : user?.plan === 'pro' ? 'Pro Plan' : 'Business Plan'}</h3>
                        <p className="text-sm text-slate-600">${user?.plan === 'basic' ? '49' : user?.plan === 'pro' ? '79' : '149'}/month</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                        <Button variant="outline" size="sm" className="remodra-button-outline flex items-center justify-center" asChild>
                          <a href="/billing">Change Plan</a>
                        </Button>
                      </div>
                    </div>

                    <Separator />
                    
                    <div className="bg-blue-50 dark:bg-slate-800/50 border border-blue-200 dark:border-slate-700 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Payment Methods & Billing</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                            All payment methods and billing history are securely managed by Stripe.
                          </p>
                          <Button variant="outline" size="sm" className="remodra-button-outline flex items-center justify-center border-blue-300 dark:border-slate-700 hover:bg-blue-100 dark:hover:bg-slate-700" asChild>
                            <a href="/billing" className="flex items-center justify-center">
                              <CreditCard className="h-4 w-4 mr-2" />
                              Manage Billing
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="team" className="space-y-4 mt-0">
                <TeamPage embedInSettings />
              </TabsContent>
              <TabsContent value="role-setup" className="space-y-4 mt-0">
                <RoleSetupPage embedInSettings />
              </TabsContent>
            </Tabs>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}